import { roleHas } from "@iagenix/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Briefcase, ChevronRight, Plus } from "lucide-react";
import { useState } from "react";
import { ClientFilter, ClientTag } from "@/components/client-filter";
import { CreateBusinessDialog } from "@/components/meta/create-business-dialog";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { queries } from "@/lib/queries";
import { formatRelative, verificationVariant } from "@/lib/utils";

export const Route = createFileRoute("/app/$workspaceId/business-managers")({
  component: BusinessManagersPage,
});

function BusinessManagersPage() {
  const { workspaceId } = Route.useParams();
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const workspace = useQuery(queries.workspace(workspaceId));
  const businesses = useQuery(queries.businesses(workspaceId, clientId));
  const clients = useQuery(queries.clients(workspaceId));
  const [criar, setCriar] = useState(false);
  const podeGerenciar = workspace.data ? roleHas(workspace.data.role, "bm:manage") : false;

  return (
    <>
      <PageHeader
        title="Business Managers"
        description="Os portfólios empresariais a que as contas Meta conectadas têm acesso. Abra um BM para gerenciar pessoas, parceiros, system users e ativos."
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => void businesses.refetch()}
              loading={businesses.isFetching && !businesses.isPending}
            >
              Atualizar
            </Button>
            {podeGerenciar ? (
              <Button onClick={() => setCriar(true)}>
                <Plus />
                Criar BM
              </Button>
            ) : null}
          </>
        }
      />
      <Card>
        <CardContent className="space-y-4">
          <ClientFilter workspaceId={workspaceId} value={clientId} onChange={setClientId} />
          {businesses.isPending ? (
            <LoadingState rows={4} />
          ) : businesses.isError ? (
            <ErrorState error={businesses.error} onRetry={() => void businesses.refetch()} />
          ) : businesses.data.length === 0 ? (
            <EmptyState
              icon={<Briefcase className="size-8" aria-hidden />}
              title={clientId ? "Nenhum BM vinculado a este cliente" : "Nenhum Business Manager"}
              description={
                clientId
                  ? "Vincule BMs ao cliente na tela Clientes."
                  : "Conecte uma conta Meta com acesso a um Business Manager, ou aguarde a sincronização terminar."
              }
              action={
                <Button asChild variant="outline">
                  <Link
                    to={clientId ? "/app/$workspaceId/clientes" : "/app/$workspaceId/integracoes"}
                    params={{ workspaceId }}
                  >
                    {clientId ? "Ir para Clientes" : "Ir para Integrações"}
                  </Link>
                </Button>
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Verificação</TableHead>
                  <TableHead>Seu papel</TableHead>
                  <TableHead className="text-right">Contas</TableHead>
                  <TableHead>Conexão</TableHead>
                  <TableHead>Sincronizado</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {businesses.data.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>
                      <Link
                        to="/app/$workspaceId/business-managers/$businessId"
                        params={{ workspaceId, businessId: b.id }}
                        className="font-medium hover:underline"
                      >
                        {b.name}
                      </Link>
                      <p className="text-xs text-muted-foreground">id {b.metaId}</p>
                    </TableCell>
                    <TableCell>
                      <ClientTag clientId={b.clientId} clients={clients.data} />
                    </TableCell>
                    <TableCell>
                      <Badge variant={verificationVariant(b.verificationStatus)}>
                        {b.verificationLabel}
                      </Badge>
                    </TableCell>
                    <TableCell>{b.roleLabel}</TableCell>
                    <TableCell className="text-right tabular-nums">{b.adAccountCount}</TableCell>
                    <TableCell className="text-muted-foreground">{b.connectionName}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatRelative(b.syncedAt)}
                    </TableCell>
                    <TableCell>
                      <Button asChild variant="ghost" size="icon" aria-label={`Abrir ${b.name}`}>
                        <Link
                          to="/app/$workspaceId/business-managers/$businessId"
                          params={{ workspaceId, businessId: b.id }}
                        >
                          <ChevronRight />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <CreateBusinessDialog open={criar} onOpenChange={setCriar} workspaceId={workspaceId} />
    </>
  );
}
